import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AppContext } from '../context/AppContext';
import { AuthContext } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';

const SITE_URL = 'https://dayclap.com';

const STATUS_STYLES = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
  opened: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Opened' },
  accepted: { bg: 'bg-green-100', text: 'text-green-700', label: '❤️ Accepted' },
  declined: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Declined' },
  planning: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Planning' },
  proposed: { bg: 'bg-pink-100', text: 'text-pink-700', label: '💌 Plan Sent' },
  confirmed: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: '🎉 Confirmed' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Cancelled' },
  replied: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: '💌 Reply Received' },
  expired: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Expired' },
};

const ACCEPTED_STATUSES = ['accepted', 'planning', 'proposed', 'confirmed'];
const CANCELABLE_STATUSES = ['pending', 'opened'];
const EDITABLE_STATUSES = ['pending', 'draft'];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric'
  });
};

const Invitations = () => {
  const { invitations, cancelInvitation } = useContext(AppContext);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [copied, setCopied] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(null);

  const sent = invitations.filter(inv => inv.direction === 'sent');
  const received = invitations.filter(inv => inv.direction === 'received');
  const accepted = invitations.filter(inv => ACCEPTED_STATUSES.includes(inv.status));
  const replied = invitations.filter(inv => inv.status === 'replied' && inv.direction === 'sent');

  const getFiltered = () => {
    switch (tab) {
      case 'sent': return sent;
      case 'received': return received;
      case 'accepted': return accepted;
      case 'replied': return replied;
      default: return invitations;
    }
  };
  const filtered = getFiltered();

  const handleCopyLink = (token) => {
    navigator.clipboard.writeText(`${SITE_URL}/invite/${token}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleCancel = async (token) => {
    setCancelling(true);
    await cancelInvitation(token);
    setCancelling(false);
    setCancelConfirm(null);
    setSelectedInvite(null);
  };

  const handleCardClick = (inv) => {
    if (inv.direction === 'sent' && inv.status === 'replied') {
      navigate(`/reply/${inv.unique_token}`);
    } else if (inv.direction === 'sent') {
      setSelectedInvite(inv);
    } else {
      navigate(`/invite/${inv.unique_token}`);
    }
  };

  const handleEdit = (inv) => {
    const flowType = inv.flow_type || 'singles';
    navigate(`/edit/${flowType}/${inv.unique_token}`);
    setSelectedInvite(null);
  };

  const getFlowEmoji = (inv) => {
    if (inv.flow_type === 'couples') return '💕';
    return '💌';
  };

  const getFlowColor = (inv) => {
    if (inv.flow_type === 'couples') return { bg: 'bg-purple-50', text: 'text-purple-500' };
    return { bg: 'bg-pink-50', text: 'text-pink-500' };
  };

  return (
    <div className="p-6 bg-gray-50 min-h-full">
      <h1 className="text-2xl font-bold text-gray-800 mb-4 mt-4">Your Invitations</h1>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { key: 'all', label: `All (${invitations.length})` },
          { key: 'sent', label: `Sent (${sent.length})` },
          { key: 'received', label: `Received (${received.length})` },
          { key: 'replied', label: `💌 Replies (${replied.length})` },
          { key: 'accepted', label: `Accepted (${accepted.length})` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
              tab === t.key ? 'bg-pink-500 text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-20 h-20 bg-pink-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon name={tab === 'accepted' ? 'Heart' : tab === 'replied' ? 'Mail' : 'Mail'} className="text-3xl text-pink-300" />
          </div>
          <h3 className="text-lg font-bold text-gray-800 mb-2">
            {tab === 'received' ? 'No Received Invitations' :
             tab === 'sent' ? 'No Sent Invitations' :
             tab === 'replied' ? 'No Replies Yet' :
             tab === 'accepted' ? 'No Accepted Invitations' : 'No Invitations Yet'}
          </h3>
          <p className="text-gray-500 mb-6">
            {tab === 'received' ? "When someone invites you, it'll show up here." :
             tab === 'replied' ? "When someone replies to your invitation, it'll appear here as an envelope." :
             tab === 'accepted' ? "Accepted invitations will appear here." :
             'Create your first special moment. ❤️'}
          </p>
          {tab !== 'received' && tab !== 'accepted' && tab !== 'replied' && (
            <button onClick={() => navigate('/home')} className="bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-3 px-6 rounded-xl shadow-lg">
              Create Invitation
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((inv, idx) => {
            const style = STATUS_STYLES[inv.status] || STATUS_STYLES.pending;
            const isSent = inv.direction === 'sent';
            const flowColor = getFlowColor(inv);
            const isReply = isSent && inv.status === 'replied';

            return (
              <motion.div
                key={inv.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                onClick={() => handleCardClick(inv)}
                className={`p-4 rounded-2xl shadow-sm border flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow ${
                  isReply
                    ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200'
                    : 'bg-white border-gray-100'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${flowColor.bg}`}>
                  {isReply ? (
                    <motion.div
                      animate={{ rotate: [0, -5, 5, 0] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                    >
                      <span className="text-2xl">💌</span>
                    </motion.div>
                  ) : (
                    <span className="text-2xl">{getFlowEmoji(inv)}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-gray-800 truncate">
                    {isReply
                      ? `Reply from ${inv.recipient_name}`
                      : isSent
                        ? `To ${inv.recipient_name || 'Someone'}`
                        : `From ${inv.sender_profile?.name || inv.sender_display_name || 'Someone special'}`
                    }
                  </h4>
                  <p className="text-xs text-gray-500 truncate">
                    {inv.occasion}
                    {inv.event_date && ` · 📅 ${formatDate(inv.event_date)}`}
                    {inv.flow_type === 'couples' && ' · 💕'}
                    {inv.flow_type === 'singles' && ' · 💌'}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  {isReply ? (
                    <div className="flex flex-col items-end gap-1">
                      {inv.reply_response && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          inv.reply_response === 'yes' ? 'bg-green-100 text-green-700' :
                          inv.reply_response === 'maybe' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {inv.reply_response === 'yes' ? '❤️ Yes' :
                           inv.reply_response === 'maybe' ? '🤔 Maybe' : '❌ No'}
                        </span>
                      )}
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                        Open →
                      </span>
                    </div>
                  ) : (
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${style.bg} ${style.text}`}>
                      {style.label}
                    </span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Sent Invitation Detail Modal */}
      <AnimatePresence>
        {selectedInvite && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4"
            onClick={() => setSelectedInvite(null)}
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden max-h-[85vh] overflow-y-auto"
            >
              <div className={`p-6 text-white ${
                selectedInvite.flow_type === 'couples'
                  ? 'bg-gradient-to-r from-purple-500 to-violet-500'
                  : 'bg-gradient-to-r from-pink-500 to-rose-500'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold uppercase tracking-wider opacity-80">
                    {selectedInvite.flow_type === 'couples' ? 'Love Card' : 'Sent Invitation'}
                  </span>
                  <button onClick={() => setSelectedInvite(null)} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
                    <SafeIcon name="X" className="w-5 h-5" />
                  </button>
                </div>
                <h3 className="text-xl font-bold">To {selectedInvite.recipient_name}</h3>
                <p className="text-white/70 text-sm mt-1">{selectedInvite.occasion}</p>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Status</span>
                  <span className={`text-sm font-bold px-3 py-1 rounded-full ${
                    (STATUS_STYLES[selectedInvite.status] || STATUS_STYLES.pending).bg
                  } ${(STATUS_STYLES[selectedInvite.status] || STATUS_STYLES.pending).text}`}>
                    {(STATUS_STYLES[selectedInvite.status] || STATUS_STYLES.pending).label}
                  </span>
                </div>

                {selectedInvite.event_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Proposed Date</span>
                    <span className="text-sm font-semibold text-purple-600 flex items-center gap-1">
                      <SafeIcon name="Calendar" className="w-3.5 h-3.5" />
                      {formatDate(selectedInvite.event_date)}
                      {selectedInvite.event_time && ` · ${selectedInvite.event_time}`}
                    </span>
                  </div>
                )}

                {selectedInvite.event_location && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Location</span>
                    <span className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                      <SafeIcon name="MapPin" className="w-3.5 h-3.5 text-pink-400" />
                      {selectedInvite.event_location}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Created</span>
                  <span className="text-sm text-gray-700">
                    {new Date(selectedInvite.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>

                {selectedInvite.message && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 font-semibold uppercase mb-1">Your Message</p>
                    <p className="text-sm text-gray-600 italic line-clamp-3">"{selectedInvite.message}"</p>
                  </div>
                )}

                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-400 font-semibold uppercase mb-2">Invite Link</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 truncate text-xs text-gray-600 font-mono">
                      {SITE_URL}/invite/{selectedInvite.unique_token}
                    </div>
                    <button
                      onClick={() => handleCopyLink(selectedInvite.unique_token)}
                      className="flex-shrink-0 bg-pink-500 text-white text-xs font-bold px-3 py-2 rounded-lg hover:bg-pink-600 transition-colors"
                    >
                      {copied ? '✓ Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  {EDITABLE_STATUSES.includes(selectedInvite.status) && (
                    <button
                      onClick={() => handleEdit(selectedInvite)}
                      className="flex-1 bg-blue-50 text-blue-600 hover:bg-blue-100 font-semibold py-3 rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
                    >
                      <SafeIcon name="Edit" className="w-4 h-4" /> Edit
                    </button>
                  )}
                  {CANCELABLE_STATUSES.includes(selectedInvite.status) && (
                    <button
                      onClick={() => setCancelConfirm(selectedInvite)}
                      className="flex-1 text-red-600 bg-red-50 hover:bg-red-100 font-semibold py-3 rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
                    >
                      <SafeIcon name="XCircle" className="w-4 h-4" /> Cancel
                    </button>
                  )}
                  <button
                    onClick={() => handleCopyLink(selectedInvite.unique_token)}
                    className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold py-3 rounded-xl shadow-lg shadow-pink-200 text-sm flex items-center justify-center gap-2"
                  >
                    <SafeIcon name="Copy" className="w-4 h-4" /> Share Link
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cancel Confirmation */}
      <AnimatePresence>
        {cancelConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-sm w-full shadow-2xl p-8 text-center"
            >
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon name="AlertTriangle" className="text-red-500 text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Cancel Invitation?</h3>
              <p className="text-gray-500 text-sm mb-6">
                This will cancel your invitation to <strong>{cancelConfirm.recipient_name}</strong>. They won't be able to respond to it.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setCancelConfirm(null)}
                  disabled={cancelling}
                  className="flex-1 bg-gray-100 text-gray-700 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors text-sm disabled:opacity-50"
                >
                  Keep It
                </button>
                <button
                  onClick={() => handleCancel(cancelConfirm.unique_token)}
                  disabled={cancelling}
                  className="flex-1 bg-red-500 text-white font-semibold py-3 rounded-xl hover:bg-red-600 transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {cancelling ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Cancelling...</>
                  ) : (
                    <>Yes, Cancel</>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Invitations;